const skillTemplateKinds = [
  "wikiSearchByKeyword",
  "wikiSearchByTitle",
  "wikiSearchByUrl",
  "knowledgeSearchByKeyword",
  "knowledgeSearchByTitle",
  "knowledgeSearchByUrl",
  "ticketMarkAsSpam",
  "ticketAddInternalNote",
  "escalateWithMessage",
  "searchArticleLanguage",
  "fetchFileFromUrl",
  "snowflakeSqlQuery",
  "getPromotions",
  "askHumanAgent",
  "calculateTimeDifference",
];

export type SkillTemplateKind = (typeof skillTemplateKinds)[number];

export interface SkillTemplate {
  name: string;
  description: string;
  slug: string;
  curl: string;
  category: SkillCategory;
  icon: string;
  example?: string;
  helpText?: string;
  featureFlag?: string;
}

export const skillTemplates: Record<SkillTemplateKind, SkillTemplate> = {
  wikiSearchByKeyword: {
    name: "Search by Keyword",
    description: "Search wiki articles by keyword.",
    slug: "wiki_search_by_keyword",
    curl: `curl is not relevant for built in skills`,
    category: "Wiki",
    icon: "🔎",
    example:
      "Find articles about product returns, troubleshooting, or specific features",
    helpText:
      "Use this skill to search through your wiki content using keywords. Perfect for finding relevant documentation when customers ask questions.",
  },
  wikiSearchByTitle: {
    name: "Search by Article Title",
    description: "Search wiki articles by their title.",
    slug: "wiki_search_by_title",
    curl: `curl is not relevant for built in skills`,
    category: "Wiki",
    icon: "📄",
    example:
      "Find specific articles like 'Password Reset Guide' or 'API Documentation'",
    helpText:
      "Use when you know the exact or partial title of a wiki article. More precise than keyword search.",
  },
  wikiSearchByUrl: {
    name: "Search by Article URL",
    description: "Search wiki articles by their URL.",
    slug: "wiki_search_by_url",
    curl: `curl is not relevant for built in skills`,
    category: "Wiki",
    icon: "🔗",
    example:
      "Search for articles using URLs like '/docs/api-setup' or '/help/billing'",
    helpText:
      "Use when you have a specific wiki URL or path to retrieve the exact article content.",
  },

  knowledgeSearchByKeyword: {
    name: "Search by Keyword",
    description: "Search knowledge base articles by keyword.",
    slug: "knowledge_search_by_keyword",
    curl: `curl is not relevant for built in skills`,
    category: "Knowledge",
    icon: "🔎",
    example:
      "Search for 'refund policy', 'installation steps', or 'troubleshooting errors'",
    helpText:
      "Search through your knowledge base using keywords to find relevant support articles and documentation.",
  },
  knowledgeSearchByTitle: {
    name: "Search by Article Title",
    description: "Search knowledge base articles by their title.",
    slug: "knowledge_search_by_title",
    curl: `curl is not relevant for built in skills`,
    category: "Knowledge",
    icon: "📄",
    example:
      "Find articles titled 'How to Cancel Subscription' or 'Getting Started Guide'",
    helpText:
      "Search for knowledge base articles when you know the specific title or partial title.",
  },
  knowledgeSearchByUrl: {
    name: "Search by Article URL",
    description: "Search knowledge base articles by their URL.",
    slug: "knowledge_search_by_url",
    curl: `curl is not relevant for built in skills`,
    category: "Knowledge",
    icon: "🔗",
    example:
      "Access articles via URLs like '/kb/setup-guide' or '/support/billing-faq'",
    helpText:
      "Direct access to knowledge base articles when you have the specific URL or path.",
  },

  ticketMarkAsSpam: {
    name: "Mark Ticket as Spam",
    description: "Mark a ticket as spam in the ticket management system.",
    slug: "ticket_mark_as_spam",
    curl: `curl is not relevant for built in skills`,
    category: "Ticket Management",
    icon: "🚫",
    example:
      "Automatically flag suspicious or spam tickets to keep your queue clean",
    helpText:
      "Use this skill to automatically identify and mark spam tickets in your support system.",
  },
  ticketAddInternalNote: {
    name: "Add Internal Note",
    description: "Add an internal note to a ticket.",
    slug: "ticket_add_internal_note",
    curl: `curl is not relevant for built in skills`,
    category: "Ticket Management",
    icon: "📝",
    example:
      "Add context, escalation notes, or internal communication to tickets",
    helpText:
      "Add internal notes that are visible only to your team, not to customers. Useful for tracking progress or adding context.",
  },
  escalateWithMessage: {
    name: "Escalate with Message",
    description:
      "Escalate the ticket while also sending a custom message to the user",
    slug: "escalate_with_message",
    curl: `curl is not relevant for built in skills`,
    category: "Ticket Management",
    icon: "📤",
    example:
      "When a user requests human support, escalate the ticket and send them a reassuring message",
    helpText:
      "Use this skill to escalate tickets while providing a custom message to the user. Perfect for urgent escalations where you want to communicate with the user immediately.",
  },
  searchArticleLanguage: {
    name: "Search Article Language",
    description:
      "Check whether a help center article exists in a target language and get the localized URL.",
    slug: "search_article_language",
    curl: `curl is not relevant for built in skills`,
    category: "Knowledge",
    icon: "🌐",
    example:
      "When sharing article links in a reply, use the conversation language to return the same article in the user's language (e.g. /hc/he/articles/... for Hebrew) when available.",
    helpText:
      "Takes an article URL (e.g. from Zendesk Guide) and a target language. Checks if that article exists in that language via the URL locale and returns either the localized URL or a message that it is not available, so you can keep the original link if needed.",
  },
  FreshdeskTicketAddInternalNote: {
    name: "Add Internal Note",
    description: "Add an internal note to a ticket.",
    slug: "freshdesk_add_internal_note",
    curl: `curl is not relevant for built in skills`,
    category: "Ticket Management - Freshdesk",
    icon: "📝",
    example:
      "Add context, escalation notes, or internal communication to tickets",
    helpText:
      "Add internal notes that are visible only to your team, not to customers. Useful for tracking progress or adding context.",
  },
  fetchFileFromUrl: {
    name: "Fetch File from URL",
    description:
      "Allow Quack to read attached files, could be used for furhter troubleshooting and resolution",
    slug: "fetch_file_from_url",
    curl: `curl is not relevant for built in skills`,
    category: "General",
    icon: "📥",
    example:
      "When user provides a file (.txt, .pdf, .docx, etc) read it and check if .....",
    helpText:
      "Retrieve file content from URLs for processing, analysis, or storage. Supports various file formats and handles authentication if needed.",
  },
  fetchGoogleDocsFile: {
    name: "Fetch Google Docs File",
    description:
      "Allow Quack to read Google Docs files, could be used for furhter troubleshooting and resolution",
    slug: "fetch_google_docs_file",
    curl: `curl is not relevant for built in skills`,
    category: "Google drive",
    icon: "📥",
    example:
      "When user asking about specific policy, get file id: XXXXXX and read the file",
    helpText:
      "Retrieve file content from Google Docs files for furhter troubleshooting and resolution.",
  },
  getCurrentTime: {
    name: "Get Current Time",
    description:
      "Get the current day and time, when the conversation or policy is related to time or date, time span or time related question",
    slug: "get_current_time",
    curl: `curl is not relevant for built in skills`,
    category: "General",
    icon: "⏱️",
    example:
      "When policy is allowed only if last purchase was within the last 30 days, get the current time and date and check if user is eligible",
    helpText:
      "Retrieve the current date and time to provide accurate responses to time-related queries or calculations.",
  },
  calculateTimeDifference: {
    name: "Calculate Time Difference",
    description:
      'Returns the duration between two dates as a human-readable string (e.g. "2 months") and numeric values (totalMilliseconds, totalSeconds).',
    slug: "calculate_time_difference",
    curl: `curl is not relevant for built in skills`,
    category: "General",
    icon: "📅",
    example:
      "Used to calculate the time difference between two dates, how long ago something was, or time elapsed since a given date, for certain policies",
    helpText:
      "Calculate the time difference between two dates. Call only when explicitly specified in policy, brief, or instructions. Accepts ISO 8601 date strings or other parseable date formats.",
    featureFlag: "calculateTimeDifference",
  },
  snowflakeSqlQuery: {
    name: "Snowflake SQL Query",
    description: "Execute SQL queries against Snowflake database.",
    slug: "snowflake_sql_query",
    curl: `curl is not relevant for built in skills`,
    category: "Database",
    icon: "❄️",
    example:
      "Query customer data, sales metrics, or analytics from your Snowflake warehouse",
    helpText:
      "Execute SQL queries against your Snowflake database. Use this skill to retrieve data, run analytics, or perform database operations.",
  },
  getPromotions: {
    name: "Get Promotions",
    description:
      "Fetch current promotions, bundles, and bulk pricing information.",
    slug: "get_promotions",
    curl: "curl is not relevant for built in skills",
    category: "General",
    icon: "↗️",
    example: "What promotions are available?",
    helpText:
      "Retrieves all active promotions and bundles from the knowledge base.",
  },
  askHumanAgent: {
    name: "Ask Human Agent",
    description:
      "When the AI cannot confidently answer, it asks a human agent for help via Slack or internal channel.",
    slug: "request_human_assist",
    curl: `curl is not relevant for built in skills`,
    category: "Human Assist",
    icon: "🙋",
    example:
      "When the AI is unsure about a policy or specific account detail, it reaches out to a human agent for help",
    helpText:
      "Enable this skill to allow the AI to request help from a human agent via Slack when it cannot confidently answer a question. The AI will wait for the human response and use it to reply to the user.",
    featureFlag: "hitlApprovalRequests",
  },
};

export const skillTemplateSlugs = Object.values(skillTemplates).map(
  (template) => template.slug
);

export const skillCategories = [
  "Wiki",
  "Knowledge",
  "Ticket Management",
  "General",
  "Google drive",
  "Database",
  "Ticket Management - Freshdesk",
  "Human Assist",
] as const;

export type SkillCategory = (typeof skillCategories)[number];
